// Designer-side view of the homeowner's Garden App for a given project.
//
// Lists every handoff for the project with its current status. For accepted
// handoffs where the homeowner has enabled designer access, the designer can
// click "Open Garden App in co-edit mode" — this stores an acting-as context in
// sessionStorage (read by src/garden/services/gardenApi.js) so all /api/garden
// requests are scoped to the homeowner's data via the x-garden-as-user header.
// The homeowner controls this access from their Garden settings screen; if
// disabled, the backend rejects the override and this button is hidden.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handoffApi } from './zonesApi';
import { setDesignerCoEdit } from '../garden/services/gardenApi';

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function StatusBadge({ status }) {
  const map = {
    pending:   { bg: '#fef3c7', fg: '#92400e', label: 'Invite pending' },
    accepted:  { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
    cancelled: { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
    expired:   { bg: '#e5e7eb', fg: '#374151', label: 'Expired' },
  };
  const s = map[status] || { bg: '#e5e7eb', fg: '#374151', label: status || 'Unknown' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 600,
        background: s.bg,
        color: s.fg,
      }}
    >
      {s.label}
    </span>
  );
}

export default function ProjectGardenTab({ projectId, projectName, onClose }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [handoffs, setHandoffs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await handoffApi.listForProject(projectId);
      setHandoffs(Array.isArray(resp.handoffs) ? resp.handoffs : []);
    } catch (e) {
      setError(e.message || 'Failed to load handoffs');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const onEnterCoEdit = (h) => {
    if (!h.accepted_user_id || h.status !== 'accepted' || !h.designer_access_enabled) return;
    setDesignerCoEdit({
      userId: h.accepted_user_id,
      projectId,
      label: `${projectName || 'Project'} · ${h.homeowner_name || h.homeowner_email}`,
    });
    navigate('/garden');
    if (typeof onClose === 'function') onClose();
  };

  const onCancel = async (h) => {
    if (!window.confirm(`Cancel the invite for ${h.homeowner_email}? They will no longer be able to accept it.`)) return;
    setBusyId(h.id);
    try {
      await handoffApi.cancel(h.id);
      await load();
    } catch (e) {
      alert(e.message || 'Failed to cancel invite');
    } finally {
      setBusyId(null);
    }
  };

  const activeHandoffs = handoffs.filter(h => h.status === 'accepted');
  const pendingHandoffs = handoffs.filter(h => h.status === 'pending');
  const otherHandoffs = handoffs.filter(h => h.status !== 'accepted' && h.status !== 'pending');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-6 shadow-xl">
        <div className="flex items-start justify-between gap-4 p-5 border-b">
          <div>
            <h2 className="text-xl font-semibold">Homeowner Garden App</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage handoffs for this project and co-edit the homeowner's
              Garden App when they've granted you access.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-6">
          {loading && (
            <div className="text-sm text-gray-500">Loading handoffs…</div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {error}
            </div>
          )}

          {!loading && !error && handoffs.length === 0 && (
            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-4">
              No handoffs yet for this project. Use the{' '}
              <strong>🌿 Send to Homeowner</strong> button on the plan to email
              the homeowner a prepopulated Garden App invite.
            </div>
          )}

          {activeHandoffs.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Active gardens ({activeHandoffs.length})
              </h3>
              <div className="space-y-2">
                {activeHandoffs.map((h) => {
                  const canCoEdit = !!(h.accepted_user_id && h.designer_access_enabled);
                  return (
                    <div key={h.id} className="border border-gray-200 rounded p-3 flex flex-wrap items-start gap-3">
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-gray-900">
                            {h.homeowner_name || h.homeowner_email}
                          </div>
                          <StatusBadge status={h.status} />
                          {!h.designer_access_enabled && (
                            <span className="text-[11px] text-gray-600 bg-gray-100 rounded px-2 py-0.5">
                              Co-edit disabled by homeowner
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {h.homeowner_email}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Accepted {formatDate(h.accepted_at)} ·{' '}
                          {Number(h.zone_count) || 0} zones ·{' '}
                          {Number(h.plant_count) || 0} plants
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEnterCoEdit(h)}
                          disabled={!canCoEdit}
                          className={
                            'px-3 py-1.5 rounded text-sm font-medium ' +
                            (canCoEdit
                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                              : 'bg-gray-200 text-gray-500 cursor-not-allowed')
                          }
                          title={
                            canCoEdit
                              ? "Open the homeowner's Garden App in co-edit mode"
                              : 'The homeowner has turned off designer access'
                          }
                        >
                          Open Garden App
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {pendingHandoffs.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Pending invites ({pendingHandoffs.length})
              </h3>
              <div className="space-y-2">
                {pendingHandoffs.map((h) => (
                  <div key={h.id} className="border border-gray-200 rounded p-3 flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-gray-900">{h.homeowner_email}</div>
                        <StatusBadge status={h.status} />
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Sent {formatDate(h.created_at)} ·{' '}
                        {Number(h.zone_count) || 0} zones ·{' '}
                        {Number(h.plant_count) || 0} plants
                        {h.expires_at ? ` · expires ${formatDate(h.expires_at)}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onCancel(h)}
                        disabled={busyId === h.id}
                        className="px-3 py-1.5 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        title="Cancel this pending invite"
                      >
                        {busyId === h.id ? 'Cancelling…' : 'Cancel invite'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {otherHandoffs.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                History ({otherHandoffs.length})
              </h3>
              <div className="space-y-2">
                {otherHandoffs.map((h) => (
                  <div key={h.id} className="border border-gray-100 rounded p-3 flex items-center gap-3 bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-gray-700">{h.homeowner_email}</div>
                        <StatusBadge status={h.status} />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatDate(h.updated_at || h.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
            title="Refresh handoff list"
          >
            🔄 Refresh
          </button>
        </div>
      </div>
    </div>
  );
}
